export function PlanCardSkeleton({ className = "", tall = false }) {
  return (
    <div
      className={`flex animate-pulse flex-col rounded-2xl border border-hc-surface-border bg-hc-page-shell p-5 ${
        tall ? "min-h-[280px]" : "min-h-[200px]"
      } ${className}`}
    >
      <div className="h-5 w-24 rounded bg-white/10" />
      <div className="mt-3 h-8 w-32 rounded bg-white/10" />
      <div className="mt-4 h-3 w-full rounded bg-white/10" />
      {tall ? (
        <div className="mt-6 flex-1 space-y-2">
          <div className="h-3 w-full rounded bg-white/10" />
          <div className="h-3 w-full rounded bg-white/10" />
          <div className="h-3 w-[85%] rounded bg-white/10" />
        </div>
      ) : null}
      <div className={`${tall ? "mt-5" : "mt-auto"} h-9 w-full rounded-[10px] bg-white/10`} />
    </div>
  );
}

export function PlanCardSkeletonGrid({ count = 2, tall = false, className = "" }) {
  return (
    <div className={`grid gap-4 sm:grid-cols-2 ${className}`}>
      {Array.from({ length: count }, (_, i) => (
        <PlanCardSkeleton key={i} tall={tall} />
      ))}
    </div>
  );
}

export function StatCardSkeleton({ className = "" }) {
  return (
    <div
      className={`animate-pulse rounded-[14px] border border-hc-surface-border bg-hc-surface p-4 ${className}`}
    >
      <div className="h-3 w-24 rounded bg-white/10" />
      <div className="mt-3 h-8 w-16 rounded bg-white/10" />
      <div className="mt-3 h-3 w-40 rounded bg-white/10" />
    </div>
  );
}

export function BillingPlanRowSkeleton() {
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-[14px] border border-hc-surface-border bg-hc-page-shell px-4 py-3.5">
      <div className="space-y-2">
        <div className="h-5 w-36 animate-pulse rounded bg-white/10" />
        <div className="h-3 w-28 animate-pulse rounded bg-white/10" />
      </div>
      <div className="h-9 w-36 animate-pulse rounded-[10px] bg-white/10" />
    </div>
  );
}

export function PaymentMethodSkeleton() {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-[14px] border border-hc-surface-border bg-hc-page-shell px-4 py-3.5">
      <div className="flex items-center gap-3">
        <div className="size-9 shrink-0 animate-pulse rounded-[10px] bg-white/10" />
        <div className="space-y-2">
          <div className="h-3.5 w-44 animate-pulse rounded bg-white/10" />
          <div className="h-3 w-24 animate-pulse rounded bg-white/10" />
        </div>
      </div>
      <div className="h-5 w-14 animate-pulse rounded-full bg-white/10" />
    </div>
  );
}

export function GradientSummarySkeleton({ actionButtons = 0 } = {}) {
  return (
    <section className="rounded-2xl border border-hc-primary/25 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="h-7 w-56 max-w-full animate-pulse rounded bg-white/10" />
          <div className="mt-2 h-3.5 w-40 animate-pulse rounded bg-white/10" />
        </div>
        <div className="h-7 w-36 animate-pulse rounded-full bg-white/10" />
      </div>
      {actionButtons > 0 ? (
        <div className="mt-6 flex flex-wrap gap-2">
          {Array.from({ length: actionButtons }, (_, i) => (
            <div key={i} className="h-9 w-32 animate-pulse rounded-[10px] bg-white/10" />
          ))}
        </div>
      ) : null}
    </section>
  );
}

export function CurrentPlanSummarySkeleton() {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <div className="h-3 w-20 animate-pulse rounded bg-white/10" />
        <div className="mt-2 h-7 w-36 animate-pulse rounded bg-white/10" />
      </div>
      <div className="space-y-2 text-right">
        <div className="ml-auto h-7 w-24 animate-pulse rounded bg-white/10" />
        <div className="ml-auto h-3 w-32 animate-pulse rounded bg-white/10" />
      </div>
    </div>
  );
}

export function ProfileFieldsSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {[0, 1].map((i) => (
        <div key={i} className="flex flex-col gap-1.5">
          <div className="h-3 w-20 animate-pulse rounded bg-white/10" />
          <div className="h-10 w-full animate-pulse rounded-[10px] bg-white/10" />
        </div>
      ))}
    </div>
  );
}

function InvoiceListRowSkeleton() {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
      <div className="flex min-w-0 items-center gap-3">
        <div className="size-5 shrink-0 animate-pulse rounded-full bg-white/10" />
        <div className="flex flex-col gap-1.5">
          <div className="h-3.5 w-32 animate-pulse rounded bg-white/10" />
          <div className="h-3 w-24 animate-pulse rounded bg-white/10" />
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="h-5 w-14 animate-pulse rounded-full bg-white/10" />
        <div className="h-3.5 w-16 animate-pulse rounded bg-white/10" />
      </div>
    </li>
  );
}

export function InvoiceListRowsSkeleton({ rows = 3 }) {
  return (
    <ul className="divide-y divide-hc-surface-border/80">
      {Array.from({ length: rows }, (_, i) => (
        <InvoiceListRowSkeleton key={i} />
      ))}
    </ul>
  );
}

export function InvoiceListSkeleton({ rows = 3, className = "" }) {
  return (
    <div className={`overflow-hidden rounded-2xl border border-hc-surface-border bg-hc-surface ${className}`}>
      <div className="border-b border-hc-surface-border px-5 py-3.5">
        <div className="h-4 w-32 animate-pulse rounded bg-white/10" />
      </div>
      <InvoiceListRowsSkeleton rows={rows} />
    </div>
  );
}

export function ListSectionSkeleton({ rows = 3, className = "" }) {
  return (
    <div
      className={`overflow-hidden rounded-2xl border border-hc-surface-border bg-hc-surface ${className}`}
    >
      <div className="border-b border-hc-surface-border px-5 py-3.5">
        <div className="h-4 w-32 animate-pulse rounded bg-white/10" />
      </div>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 border-b border-hc-surface-border/80 px-5 py-4 last:border-b-0"
        >
          <div className="size-10 shrink-0 animate-pulse rounded-[10px] bg-white/10" />
          <div className="flex flex-1 flex-col gap-1.5">
            <div className="h-3.5 w-40 animate-pulse rounded bg-white/10" />
            <div className="h-3 w-28 animate-pulse rounded bg-white/10" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ProgramDetailSkeleton() {
  return (
    <div className="relative mx-auto max-w-5xl px-6 pb-16 pt-10">
      <div className="grid gap-8 lg:grid-cols-[1fr_300px]">
        <div>
          <div className="mb-6 h-44 w-full animate-pulse rounded-2xl bg-white/10" />
          <div className="mb-3 flex gap-2">
            <div className="h-6 w-20 animate-pulse rounded-full bg-white/10" />
            <div className="h-6 w-28 animate-pulse rounded-full bg-white/10" />
          </div>
          <div className="h-9 w-3/4 max-w-md animate-pulse rounded bg-white/10" />
          <div className="mt-2 h-4 w-32 animate-pulse rounded bg-white/10" />
          <div className="mt-6 space-y-2">
            <div className="h-3 w-full animate-pulse rounded bg-white/10" />
            <div className="h-3 w-full animate-pulse rounded bg-white/10" />
            <div className="h-3 w-4/5 animate-pulse rounded bg-white/10" />
          </div>
        </div>
        <div className="rounded-2xl border border-hc-surface-border-strong bg-hc-surface p-6">
          <div className="h-9 w-24 animate-pulse rounded bg-white/10" />
          <div className="mt-2 h-3 w-32 animate-pulse rounded bg-white/10" />
          <div className="mt-5 h-[52px] w-full animate-pulse rounded-[14px] bg-white/10" />
        </div>
      </div>
    </div>
  );
}

export function ProgramsPageSkeleton() {
  return (
    <>
      <section className="grid gap-4 sm:grid-cols-2">
        <StatCardSkeleton />
        <StatCardSkeleton />
      </section>
      <section className="mt-4 overflow-hidden rounded-2xl border border-hc-surface-border bg-hc-surface">
        <div className="border-b border-hc-surface-border px-5 py-3.5">
          <div className="h-4 w-36 animate-pulse rounded bg-white/10" />
        </div>
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-4 border-b border-hc-surface-border/80 px-5 py-4 last:border-b-0">
            <div className="h-12 w-16 shrink-0 animate-pulse rounded-[10px] bg-white/10" />
            <div className="flex flex-1 flex-col gap-1.5">
              <div className="h-3.5 w-48 animate-pulse rounded bg-white/10" />
              <div className="h-3 w-24 animate-pulse rounded bg-white/10" />
            </div>
          </div>
        ))}
      </section>
    </>
  );
}

export function ClientBillingLoadingSkeleton() {
  return (
    <section className="rounded-2xl border border-hc-surface-border bg-hc-surface p-6">
      <div className="h-4 w-48 animate-pulse rounded bg-white/10" />
      <div className="mt-2 h-3 w-72 max-w-full animate-pulse rounded bg-white/10" />
      <div className="mt-5">
        <PlanCardSkeletonGrid count={1} />
      </div>
    </section>
  );
}

export function StripeConnectActionsSkeleton() {
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      <div className="h-9 w-32 animate-pulse rounded-[10px] bg-white/10" />
      <div className="h-9 w-36 animate-pulse rounded-[10px] bg-white/10" />
    </div>
  );
}

export function SidebarActionSkeleton() {
  return <div className="mt-5 h-[52px] w-full animate-pulse rounded-[14px] bg-white/10" />;
}
